import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { scroller } from 'react-scroll';
import { useSelector } from 'react-redux';
import ReactTimeout from 'react-timeout';
import classNames from 'classnames';
import _ from 'lodash';
import { getCommonTealiumProps } from 'helpers/AccountHelper';

import {
  Events,
  getRecommendationId,
  handleAnalytics,
  SEARCH_INPUT_TRIGGER_INTERVAL_MS,
} from '@foxcorp/delta-web-analytics';

import {
  addDays,
  addHours,
  differenceInMinutes,
  getHours,
  getMinutes,
  isAfter,
  isBefore,
  isSameDay,
  isSameSecond,
  midNightTonightTime,
  msToDate,
  now,
  setDateAndTime,
  SEVEN_PM,
  startOfDay,
  stringToDate,
  subHours,
  tonight7PMTime,
} from 'helpers/DateTimeHelper';
import { isStaticRender, getOomLivePlayerEnabled } from 'helpers/ContentHelper';
import StrikeBanner, { STRIKE_PAGE_TYPE_SCHEDULE } from 'components/StrikeBanner';
import {
  getStartingTs,
  isDayAfter,
  isToday,
  isTomorrow,
  MINUTES_INTERVAL,
} from 'screens/Live/LiveHelpers';

import FutureCellModal from './components/FutureCellModal';
import SearchResults from './components/SearchResults';
import FilterSearchBar from './components/FilterSearchBar';
import EPGGrid from './components/EPGGrid';
import LiveCollections from './components/LiveCollections';
import Style from './FoxLive.module.scss';

const FoxLive = ({
  accessToken,
  account,
  breakpoint,
  clearInterval,
  defaultProgram,
  disableScroll,
  favoritesManager,
  fetchLiveAction,
  globalVideo,
  isAuthenticated,
  location,
  overlay,
  preauthorizedResources,
  saveLiveRestart,
  selectedChannel,
  selectedVideo,
  setInterval,
  setOverlay,
  setVideoCountdown,
  ...rest
}) => {
  let selectedVideoData = null;
  if (globalVideo) {
    selectedVideoData = globalVideo;
  }

  const [modalVisibility, setModalVisibility] = useState(false);
  const [currentVideo, setCurrentVideo] = useState({});
  const [panelCount, setPanelCount] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [selectedDate, setSelectedDateState] = useState(now());
  const [epgGridPage, setEpgGridPage] = useState(1);
  const [startingTs, setStartingTs] = useState(getStartingTs());
  const [startingDate, setStartingDate] = useState(now());
  const [showPlayButton, setShowPlayButton] = useState(false);

  const { panels } = useSelector(state => state.live);
  const { recommendationIdData } = useSelector(state => state.app);
  const recommendationId = getRecommendationId(recommendationIdData);
  const EPG_SCROLL_HOUR_JUMP = 1.5;
  const EPG_TOTAL_DAYS = 3;
  const EPG_TOTAL_HOURS = 0;
  const [endingDate] = useState(
    addHours(addDays(startOfDay(now()), EPG_TOTAL_DAYS), EPG_TOTAL_HOURS),
  );

  useEffect(() => {
    if (modalVisibility) {
      disableScroll({ disableScroll: true, bodyOnly: true });
    } else {
      disableScroll({ disableScroll: false });
    }
    return () => disableScroll({ disableScroll: false });
  }, [disableScroll, modalVisibility]);

  useEffect(() => {
    let inputChangeTimeout = null;
    if (searchInput.length > 1 && !searchCompleted) {
      inputChangeTimeout = setTimeout(() => {
        handleAnalytics({
          event: Events.SEARCH_COMPLETED,
          payload: {
            page_search_term: searchInput,
            page_num_search_results: panelCount,
          },
          tealiumPayload: {
            ...getCommonTealiumProps(),
          },
        });

        setSearchCompleted(true);
      }, SEARCH_INPUT_TRIGGER_INTERVAL_MS);
    } else if (searchInput.length < 2) {
      setSearchCompleted(false);
    }
    return () => clearTimeout(inputChangeTimeout);
  }, [panelCount, searchCompleted, searchInput]);

  useEffect(() => {
    let showPlayButtonInterval = null;
    if (!_.isEmpty(currentVideo)) {
      const { startDate } = currentVideo;
      showPlayButtonInterval = setInterval(() => {
        const minutesLeft = differenceInMinutes(startDate, new Date());
        const shouldShowPlayButton = minutesLeft && minutesLeft <= 5;
        if (shouldShowPlayButton) {
          setShowPlayButton(true);
        } else {
          setShowPlayButton(false);
        }
      }, 1000);
    }
    return () => clearInterval(showPlayButtonInterval);
  }, [currentVideo, clearInterval, setInterval]);

  const handleOnClearSearch = useCallback(() => {
    setSearchInput('');
    document.getElementById('searchEPG').value = '';
  }, []);

  const handleOnSelectVideo = useCallback(video => {
    if (video) {
      setCurrentVideo(video);
      setModalVisibility(true);
    }
  }, []);

  const checkAtStarting = useCallback(
    date => {
      const startingHours = getHours(startingDate);
      const startingMinutes = getMinutes(startingDate);
      const startingTime = startingHours * 60 + Math.floor(startingMinutes / MINUTES_INTERVAL);
      const dateHours = getHours(date);
      const dateMinutes = getMinutes(startingDate);
      const time = dateHours * 60 + Math.floor(dateMinutes / MINUTES_INTERVAL);
      return isSameDay(startingDate, date) && startingTime >= time;
    },
    [startingDate],
  );

  const atStartDate = checkAtStarting(selectedDate);
  const gridDateStart = msToDate(startingTs * 1000);
  const lastGridStart = addHours(endingDate, -2);
  const atEndDate = gridDateStart >= lastGridStart;

  const setSelectedDate = useCallback(
    newSelectedDate => {
      const oldDate = selectedDate || now();
      const newDate = newSelectedDate || now();
      const currentDate = now();
      if (!isSameSecond(oldDate, newDate)) {
        const selectedDate = isBefore(newDate, currentDate) ? currentDate : newDate;
        setSelectedDateState(selectedDate);
      }
    },
    [selectedDate, setSelectedDateState],
  );

  const changeDate = useCallback(
    date => {
      setSelectedDate(stringToDate(date));
      setStartingTs(getStartingTs(date));
    },
    [setSelectedDate, setStartingTs],
  );

  const gridStart = useCallback(() => {
    changeDate(now());
    setEpgGridPage(1);
  }, [changeDate, setEpgGridPage]);

  const gridTonight = useCallback(() => {
    // advances to 7p local time the same day
    if (
      isBefore(new Date(now()).getTime(), tonight7PMTime(selectedDate)) ||
      (isAfter(new Date(selectedDate).getTime(), midNightTonightTime) &&
        isBefore(new Date(now()).getTime(), tonight7PMTime(selectedDate)))
    ) {
      changeDate(addHours(setDateAndTime(now(), SEVEN_PM), 0.1));

      if (isToday(selectedDate)) {
        setEpgGridPage(epgGridPage + 1);
      }

      if (isTomorrow(selectedDate) || isDayAfter(selectedDate)) {
        setEpgGridPage(epgGridPage - 1);
      }
    }
  }, [changeDate, selectedDate, setEpgGridPage, epgGridPage]);

  const gridTomorrow = useCallback(() => {
    // advances to 7p local time the next day
    if (!atEndDate) {
      const tomorrow7PM = addDays(setDateAndTime(now(), SEVEN_PM), 1);
      changeDate(addHours(tomorrow7PM, 0.39));

      if (isToday(selectedDate)) {
        setEpgGridPage(epgGridPage + 1);
      }

      if (isTomorrow(selectedDate) || isDayAfter(selectedDate)) {
        setEpgGridPage(epgGridPage - 1);
      }
    }
  }, [atEndDate, changeDate, selectedDate, setEpgGridPage, epgGridPage]);

  const gridDayAfter = useCallback(() => {
    // advances to 7p local time the day after next (2 days ahead)
    if (!atEndDate) {
      const dayAfter = addDays(setDateAndTime(now(), SEVEN_PM), 2);
      changeDate(addHours(dayAfter, 0.39));

      if (isToday(selectedDate) || isTomorrow(selectedDate)) {
        setEpgGridPage(epgGridPage + 1);
      }
    }
  }, [atEndDate, changeDate, selectedDate, setEpgGridPage, epgGridPage]);

  const gridNext = useCallback(() => {
    const newDate = addHours(selectedDate, EPG_SCROLL_HOUR_JUMP);
    const newEpgGridPage = epgGridPage + 1;
    changeDate(newDate);
    setEpgGridPage(newEpgGridPage);
  }, [changeDate, selectedDate, setEpgGridPage, epgGridPage]);

  const gridPrev = useCallback(() => {
    let newDate = subHours(selectedDate, EPG_SCROLL_HOUR_JUMP);
    const newEpgGridPage = epgGridPage - 1;
    if (checkAtStarting(newDate)) {
      newDate = startingDate;
      changeDate(newDate);
      setEpgGridPage(1);
    } else {
      changeDate(newDate);
      setEpgGridPage(newEpgGridPage);
    }
  }, [changeDate, checkAtStarting, selectedDate, startingDate, setEpgGridPage, epgGridPage]);

  const outOfMarketLivePlayerData = getOomLivePlayerEnabled();

  const handleOnFieldChange = useCallback(e => {
    const handleOnSearchChange = _.debounce(text => {
      setSearchInput(text);
      scroller.scrollTo('searchResults', {
        duration: 150,
        delay: 0,
        offset: -450,
        smooth: true,
        containerId: 'live-page-overflow',
      });
    }, 500);

    return handleOnSearchChange(e.target.value);
  }, []);

  const handleOnCloseModal = useCallback(() => {
    setCurrentVideo(null);
    setModalVisibility(false);
  }, []);

  return (
    <>
      <div className={classNames(Style.pinnedContentContainer, 'stickyContainer')}>
        {!outOfMarketLivePlayerData && (
          <FilterSearchBar
            atStartDate={atStartDate}
            atEndDate={atEndDate}
            gridNext={gridNext}
            gridPrev={gridPrev}
            gridStart={gridStart}
            gridTonight={gridTonight}
            gridTomorrow={gridTomorrow}
            gridDayAfter={gridDayAfter}
            onFieldChange={handleOnFieldChange}
            onClearSearch={handleOnClearSearch}
            searchInput={searchInput}
            selectedDate={selectedDate}
          />
        )}
      </div>
      {!outOfMarketLivePlayerData && (
        <>
          <FutureCellModal
            favoritesManager={favoritesManager}
            globalVideo={globalVideo}
            onClose={handleOnCloseModal}
            recommendationId={recommendationId}
            setVideoCountdown={setVideoCountdown}
            showPlayButton={showPlayButton}
            video={currentVideo}
            visibility={modalVisibility}
          />
          {!searchInput || searchInput.length < 2 ? (
            <>
              <EPGGrid
                {...rest}
                account={account}
                breakpoint={breakpoint}
                changeDate={changeDate}
                checkAtStarting={checkAtStarting}
                defaultProgram={defaultProgram}
                endingDate={endingDate}
                epgGridPage={epgGridPage}
                favoritesManager={favoritesManager}
                fetchLiveAction={fetchLiveAction}
                globalVideo={globalVideo}
                gridStart={gridStart}
                isAuthenticated={isAuthenticated}
                isStatic={isStaticRender()}
                location={location}
                overlay={overlay}
                preauthorizedResources={preauthorizedResources}
                saveLiveRestart={saveLiveRestart}
                selectedChannel={selectedChannel}
                selectedDate={selectedDate}
                selectedVideoData={selectedVideoData}
                setCurrentVideo={handleOnSelectVideo}
                setEpgGridPage={setEpgGridPage}
                setOverlay={setOverlay}
                setSelectedDate={setSelectedDate}
                setStartingDate={setStartingDate}
                setStartingTs={setStartingTs}
                startingTs={startingTs}
              />
              <LiveCollections
                accessToken={accessToken}
                panels={panels}
                breakpoint={breakpoint}
                favoritesManager={favoritesManager}
              />
            </>
          ) : null}

          {searchInput?.length > 1 ? (
            <SearchResults
              accessToken={accessToken}
              breakpoint={breakpoint}
              favoritesManager={favoritesManager}
              onClearSearch={handleOnClearSearch}
              panelCount={panelCount}
              searchInput={searchInput}
              searchCompleted={searchCompleted}
              setSearchCompleted={setSearchCompleted}
              setCurrentVideo={handleOnSelectVideo}
              setPanelCount={setPanelCount}
            />
          ) : null}
        </>
      )}
      <StrikeBanner index={1} pageType={STRIKE_PAGE_TYPE_SCHEDULE} breakpoint={breakpoint} />
    </>
  );
};

FoxLive.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  breakpoint: PropTypes.string,
  clearInterval: PropTypes.func,
  coordinates: PropTypes.shape({}),
  defaultProgram: PropTypes.string,
  deviceId: PropTypes.string,
  disableScroll: PropTypes.func,
  favoritesManager: PropTypes.shape({}),
  fetchLiveAction: PropTypes.func,
  globalVideo: PropTypes.shape({}),
  isAuthenticated: PropTypes.bool,
  location: PropTypes.shape({}),
  onChannelSelect: PropTypes.func,
  overlay: PropTypes.string,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  saveLiveRestart: PropTypes.func.isRequired,
  selectedChannel: PropTypes.number,
  selectedVideo: PropTypes.string,
  setInterval: PropTypes.func,
  setOverlay: PropTypes.func.isRequired,
  setVideoCountdown: PropTypes.func,
};

const getChangingProps = props => {
  const {
    accessToken,
    account,
    breakpoint,
    coordinates,
    defaultProgram,
    deviceId,
    favoritesManager,
    globalVideo,
    isAuthenticated,
    location,
    overlay,
    preauthorizedResources,
    selectedChannel,
    selectedVideo,
  } = props;
  return {
    accessToken,
    account,
    breakpoint,
    coordinates,
    defaultProgram,
    deviceId,
    favoritesManager,
    globalVideo,
    isAuthenticated,
    location,
    overlay,
    preauthorizedResources,
    selectedChannel,
    selectedVideo,
  };
};

export default ReactTimeout(
  React.memo(FoxLive, (props, nextProps) => {
    const oldProps = getChangingProps(props);
    const newProps = getChangingProps(nextProps);
    return _.isEqual(oldProps, newProps);
  }),
);
